import type maplibregl from 'maplibre-gl';

/**
 * Minimal structural types for the Mappedin SDK surface we consume.
 *
 * Mirrors the slim wrappers used in `apps/directory-applet/src/hooks/useMappedinRenderer.ts`.
 * Mappedin's published types are extremely broad and tied to internal MVF
 * generics; defining only what we touch keeps the call sites typesafe
 * without dragging the entire surface into our build.
 */

export interface MappedinFloor {
  id: string;
  name?: string;
  elevation?: number;
}

export interface MappedinNode {
  coordinate?: { latitude: number; longitude: number };
}

export interface MappedinLocation {
  type?: string;
  amenity?: string;
  name?: string;
  nodes?: MappedinNode[];
}

export interface MappedinMapData {
  getByType(type: string): unknown[];
}

interface MappedinCamera {
  setScreenOffsets?(offsets: { top: number; bottom: number; left: number; right: number }): void;
  animateTo(target: Record<string, unknown>, options?: Record<string, unknown>): Promise<void>;
  focusOn(targets: unknown[], options?: Record<string, unknown>): Promise<void>;
}

interface MappedinMarkers {
  add(coordinate: unknown, template: string, options?: Record<string, unknown>): unknown;
  remove(item: unknown): void;
}

interface MappedinOutdoor {
  map?: maplibregl.Map;
}

interface MappedinClickEvent {
  markers?: unknown[];
}

export interface MappedinMapView {
  Camera: MappedinCamera;
  Markers: MappedinMarkers;
  Outdoor?: MappedinOutdoor;
  on(event: string, listener: (event: unknown) => void): void;
  off(event: string, listener: (event: unknown) => void): void;
  setFloor(floor: unknown): void;
  updateState(target: unknown, state: Record<string, unknown>): void;
  destroy?(): void;
}

/**
 * Type guard for click events that carry `markers` (vs space/node clicks).
 */
export function hasMarkers(event: unknown): event is MappedinClickEvent {
  return (
    typeof event === 'object' &&
    event !== null &&
    'markers' in event &&
    Array.isArray((event as MappedinClickEvent).markers)
  );
}
